import { useState, useEffect, useCallback } from 'react';
import api from '../../../../utils/api';

export function useDashboardData(period = '30d') {
  const [data, setData] = useState({
    kpis: null,
    health: null,
    funnel: null,
    alerts: null,
    topAgents: null,
    counselorWorkload: null,
    topUniversities: null,
    topCountries: null,
    trends: null,
    recentActivity: null,
    auditLog: null,
    contentMetrics: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchKPIs = useCallback(async () => {
    try {
      const res = await api.get(`/admin/dashboard/kpis?period=${period}`);
      setData(prev => ({ ...prev, kpis: res.data.data }));
    } catch (err) {
      console.error('Failed to fetch KPIs:', err);
    }
  }, [period]);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await api.get('/admin/dashboard/health');
      setData(prev => ({ ...prev, health: res.data.data }));
    } catch (err) {
      console.error('Failed to fetch health:', err);
    }
  }, []);

  const fetchFunnel = useCallback(async () => {
    try {
      const res = await api.get(`/admin/dashboard/funnel?period=${period}`);
      setData(prev => ({ ...prev, funnel: res.data.data }));
    } catch (err) {
      console.error('Failed to fetch funnel:', err);
    }
  }, [period]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await api.get('/admin/dashboard/alerts');
      setData(prev => ({ ...prev, alerts: res.data.data }));
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    }
  }, []);

  const fetchOperationalData = useCallback(async () => {
    try {
      const [agents, counselors, universities, countries, content] = await Promise.all([
        api.get(`/admin/dashboard/top-agents?period=${period}&limit=5`),
        api.get('/admin/dashboard/counselor-workload?limit=5'),
        api.get('/admin/dashboard/top-universities?limit=5'),
        api.get('/admin/dashboard/top-countries?limit=5'),
        api.get('/admin/dashboard/content-metrics')
      ]);
      setData(prev => ({
        ...prev,
        topAgents: agents.data.data,
        counselorWorkload: counselors.data.data,
        topUniversities: universities.data.data,
        topCountries: countries.data.data,
        contentMetrics: content.data.data
      }));
    } catch (err) {
      console.error('Failed to fetch operational data:', err);
    }
  }, [period]);

  const fetchTrends = useCallback(async () => {
    try {
      const res = await api.get(`/admin/dashboard/trends?period=${period}&metrics=students,applications,revenue`);
      setData(prev => ({ ...prev, trends: res.data.data }));
    } catch (err) {
      console.error('Failed to fetch trends:', err);
    }
  }, [period]);

  const fetchRecentActivity = useCallback(async () => {
    try {
      const res = await api.get('/admin/dashboard/recent-activity?limit=5');
      setData(prev => ({ ...prev, recentActivity: res.data.data }));
    } catch (err) {
      console.error('Failed to fetch recent activity:', err);
    }
  }, []);

  const fetchAuditLog = useCallback(async () => {
    try {
      const res = await api.get('/admin/dashboard/audit-log?limit=10');
      setData(prev => ({ ...prev, auditLog: res.data.data }));
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchKPIs(),
        fetchHealth(),
        fetchFunnel(),
        fetchAlerts(),
        fetchOperationalData(),
        fetchTrends(),
        fetchRecentActivity(),
        fetchAuditLog()
      ]);
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [fetchKPIs, fetchHealth, fetchFunnel, fetchAlerts, fetchOperationalData, fetchTrends, fetchRecentActivity, fetchAuditLog]);

  useEffect(() => {
    fetchAllData();

    // Set up polling intervals
    const kpiInterval = setInterval(fetchKPIs, 60000); // 1 minute
    const healthInterval = setInterval(fetchHealth, 30000); // 30 seconds
    const alertsInterval = setInterval(fetchAlerts, 30000); // 30 seconds
    const activityInterval = setInterval(fetchRecentActivity, 15000); // 15 seconds

    return () => {
      clearInterval(kpiInterval);
      clearInterval(healthInterval);
      clearInterval(alertsInterval);
      clearInterval(activityInterval);
    };
  }, [fetchAllData, fetchKPIs, fetchHealth, fetchAlerts, fetchRecentActivity]);

  const refresh = useCallback(() => {
    fetchAllData();
  }, [fetchAllData]);

  return {
    ...data,
    loading,
    error,
    refresh
  };
}

export default useDashboardData;
